// src/pages/ia-paca/metiers/data-engineer-ia.tsx
//
// Page métier — Data Engineer IA en PACA.

import MetierPageTemplate, {
  type MetierPageConfig,
} from '../../../components/ia-paca/MetierPageTemplate';
import { Database, Workflow, Cloud, Activity, GitBranch, Layers } from 'lucide-react';

const CONFIG: MetierPageConfig = {
  slug: 'data-engineer-ia',
  metaTitle: 'Recrutement Data Engineer IA en PACA — Marseille, Sophia | recruteurs.io',
  metaDescription:
    "Recruter un Data Engineer IA en PACA : profils dbt, Airflow, Snowflake, BigQuery, Kafka, streaming. Pipelines qui alimentent vos modèles ML et systèmes RAG. Salaire 50-95k€.",
  metaKeywords:
    'recrutement Data Engineer IA, Data Engineer Marseille, Data Engineer Sophia, salaire Data Engineer 2026, recruter Data Engineer IA PACA',
  heroLabel: 'Data Engineer IA',
  heroSubtitle:
    "Pas un Data Scientist qui ferait des pipelines. Pas un Backend Engineer qui aurait vu Airflow. Un ingénieur dont le métier est de livrer des données fraîches, fiables, lineagées, prêtes à être consommées par vos modèles ML et systèmes RAG.",
  heroBadges: ['dbt', 'Airflow', 'Snowflake', 'Streaming', 'Vector ingestion'],
  jsonLdName: 'Data Engineer IA',
  jsonLdDescription:
    "Le Data Engineer IA construit et maintient les pipelines de données qui alimentent les modèles ML et systèmes IA. Garant de la qualité, la fraîcheur, le lineage des données.",
  jsonLdSkills:
    'Python, SQL (avancé), dbt, Airflow / Prefect / Dagster, Snowflake / BigQuery / Databricks, Kafka / Kinesis, Spark, vector ingestion (chunking, embeddings, pgvector / Pinecone / Qdrant), data quality (Great Expectations, Soda), data contracts',
  jsonLdMinSalary: 50000,
  jsonLdMedianSalary: 70000,
  jsonLdMaxSalary: 95000,
  jsonLdEducation: 'Ingénieur informatique, mathématiques appliquées, ou autodidacte avec portfolio.',
  jsonLdExperience: 'Junior (1-2 ans) à Senior (8+ ans).',
  intro: {
    title: 'Sans Data Engineer IA, vos modèles meurent en silence.',
    paragraphs: [
      "Le Data Engineer IA est <strong>le maillon le plus sous-estimé</strong> d'une équipe IA. Quand il est absent, vos modèles s'entraînent sur des données pourries, vos RAG indexent des documents obsolètes, vos pipelines tournent en silence sans alerte quand un fichier source change de schéma.",
      "Concrètement : il modélise vos données dans le warehouse (dbt + couche sémantique), automatise l'ingestion depuis vos systèmes (CDC, batch, streaming), instrumente la qualité (Great Expectations, Soda, dbt tests), gère le lineage (OpenLineage, dbt docs), et — spécificité IA — sait construire les pipelines de chunking + embeddings pour vos RAG.",
      "<strong>Différence avec un Data Engineer classique :</strong> compréhension de ce que les modèles attendent en input. Un Data Engineer pur ne pense pas naturellement à la temporalité, au leakage, à la fenêtre de prédiction. Un Data Engineer IA si.",
    ],
  },
  stackTitle: 'Les outils que votre Data Engineer IA doit maîtriser.',
  stack: [
    {
      Icon: Database,
      title: 'Warehouse / lakehouse',
      tools: ['Snowflake', 'BigQuery', 'Databricks', 'Redshift', 'Iceberg', 'DuckDB'],
    },
    {
      Icon: Layers,
      title: 'Transformation & modélisation',
      tools: ['dbt (core + cloud)', 'SQLMesh', 'Spark', 'Polars / pandas', 'SQL avancé (CTE, window fns)'],
    },
    {
      Icon: Workflow,
      title: 'Orchestration',
      tools: ['Airflow', 'Prefect', 'Dagster', 'Kestra', 'Argo Workflows'],
    },
    {
      Icon: Cloud,
      title: 'Streaming & ingestion',
      tools: ['Kafka / Kinesis', 'Flink', 'Debezium / Fivetran', 'Airbyte', 'CDC', 'Estuary'],
    },
    {
      Icon: Activity,
      title: 'Qualité & observabilité',
      tools: ['Great Expectations', 'Soda', 'dbt tests', 'Monte Carlo / Bigeye', 'OpenLineage'],
    },
    {
      Icon: GitBranch,
      title: 'Vector & embeddings (spécifique IA)',
      tools: ['pgvector', 'Pinecone', 'Qdrant', 'Weaviate', 'chunking strategies', 'embeddings (OpenAI, Cohere, BGE)'],
    },
  ],
  salaryByCityTitle: '50-95 k€, premium sur expérience streaming + vector ingestion mature.',
  salaryByCity: [
    { ville: 'Marseille', junior: '50 – 58 k€', confirme: '58 – 75 k€', senior: '75 – 92 k€' },
    { ville: 'Aix-en-Provence', junior: '48 – 56 k€', confirme: '56 – 72 k€', senior: '72 – 90 k€' },
    { ville: 'Sophia Antipolis', junior: '52 – 62 k€', confirme: '62 – 80 k€', senior: '80 – 98 k€' },
    { ville: 'Nice', junior: '50 – 58 k€', confirme: '58 – 75 k€', senior: '75 – 92 k€' },
  ],
  questionsTitle: '5 questions pour qualifier un Data Engineer IA.',
  questionsIntro:
    "Ces questions séparent un Data Engineer IA mature d'un Data Engineer généraliste qui n'a jamais travaillé avec des Data Scientists exigeants.",
  questions: [
    {
      q: "Tu dois alimenter un RAG sur 10 000 contrats PDF qui sont mis à jour quotidiennement. Quel pipeline tu mets en place ?",
      good:
        "Décrit : 1) source (S3 + listing CDC ou notification webhook), 2) extraction texte (Apryse, Unstructured, Marker), 3) chunking adapté (par section, par paragraphe selon document), 4) embeddings (modèle + version trackés), 5) upsert dans vector DB avec metadata (ACL, version document), 6) gestion des suppressions, 7) backfill + incrémental, 8) monitoring qualité.",
      bad: "'On extrait le texte, on chunke à 1000 tokens, on push dans Pinecone.' Pas de notion CDC, pas de gestion des updates/deletes, pas de monitoring.",
    },
    {
      q: 'Ton modèle de churn est en prod et ses prédictions deviennent moins fiables. Qu\'est-ce qui peut clocher côté data ?',
      good:
        "Pense : drift des features (distribution change), changement schéma source (colonne ajoutée/retirée), data freshness (pipeline en retard), data quality (valeurs nulles inhabituelles, déduplications), leakage post-déploiement, fenêtre de calcul des features qui dérive. Sait instrumenter ça.",
      bad: "'C'est le modèle qui dérive, faut le réentraîner.' Pas de notion d'investigation data en amont.",
    },
    {
      q: "Quelle est la différence entre incremental vs full refresh dans dbt, et quand tu choisis quoi ?",
      good:
        "Incremental = ajout / update sur les nouvelles lignes, plus rapide, plus économique, mais nécessite une stratégie de unique key + filtre sur is_incremental(). Full refresh = recalcul complet, plus simple, idéal pour les petits volumes ou les transformations qui changent souvent. Sait expliquer les pièges (late arriving data, backfill).",
      bad: "Ne connaît pas le concept ou ne sait pas expliquer les trade-offs.",
    },
    {
      q: 'Comment tu gères les data contracts entre les producteurs (back-end apps) et les consommateurs (Data Science) ?',
      good:
        "Connaît le concept : schéma versionné, ownership clair, validation (Great Expectations / dbt tests / Schema Registry pour Kafka), monitoring breaking changes. Cite des outils ou pratiques (Soda, Bigeye, dbt contracts, JSON Schema, Avro). Tient compte de la dimension organisationnelle.",
      bad: "'Quand ça casse, on se prévient en Slack.' Pas de process, dette permanente.",
    },
    {
      q: "Tu hérites d'un Airflow avec 200 DAGs, certains qui plantent depuis des semaines sans que personne ne s'en rende compte. Tu fais quoi ?",
      good:
        "Méthodique : 1) audit complet (DAGs actifs / inactifs / plantés / valeur business), 2) classification (critiques / secondaires / dépréciés), 3) ménage progressif (suppression, archivage), 4) ownership clair (un DAG = une équipe), 5) monitoring + alerting Slack/PagerDuty sur DAGs critiques, 6) review process pour les nouveaux DAGs.",
      bad: "'On relance ceux qui plantent.' Pas de stratégie, dette ressurgit.",
    },
  ],
  ctaTitle: "Vous cherchez un Data Engineer IA en PACA ?",
  ctaSubtitle:
    "Profil souvent confondu avec un Data Engineer classique. Notre court-list filtre sur la maturité IA et l'expérience vector ingestion. 4-6 candidats en 15-25 jours.",
};

export default function DataEngineerIaPage() {
  return <MetierPageTemplate config={CONFIG} />;
}
